/**
 * TrustGrid Settings Page
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Card, Input, Select, Button, Spin, Switch, InputNumber, Tag, Tooltip } from 'antd';
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import TrustGridPage from '../../../components/layout/TrustGridPage';
import ProjectSelector from './ProjectSelector';
import { showSuccess, showError } from '../../../utils/notifications';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { getSettings, updateSetting, generateApiKey, getDataProcessingPolicy, updateDataProcessingPolicy } from '../api/trustgridApi';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Option } = Select;

function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;

  // Build breadcrumbs from navigation state or use defaults
  const breadcrumbs = useMemo(() => {
    const baseBreadcrumbs = location.state?.breadcrumbs || [
      { label: 'TrustGrid', path: '/trustgrid/dashboard' },
    ];
    return [...baseBreadcrumbs, { label: 'Settings', path: '/trustgrid/settings' }];
  }, [location.state]);

  const [loading, setLoading] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingRegion, setSavingRegion] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [savingDataProcessing, setSavingDataProcessing] = useState(false);

  const [scanSchedule, setScanSchedule] = useState('manual');
  const [slackWebhook, setSlackWebhook] = useState('');
  const [emailRecipients, setEmailRecipients] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [apiKeys, setApiKeys] = useState([]);

  // Data Processing Policy state
  const [dataPolicy, setDataPolicy] = useState({
    default_storage_tier: 'metadata_only',
    auto_mask_pii: true,
    mask_critical_secrets: true,
    critical_secret_policy: 'store_only_hash',
    auto_archive_days: 90,
    archived_data_retention_days: 730,
    default_scan_window: 'last_7_days',
    enable_in_memory_scanning: true,
    enable_on_demand_scanning: true,
    gdpr_mode: false,
    hipaa_mode: false
  });

  useEffect(() => {
    fetchSettings();
    fetchDataProcessingPolicy();
  }, [projectId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getSettings(projectId);
      const settings = response.data.settings || {};

      setScanSchedule(settings.scan_schedule || 'manual');
      setSlackWebhook(settings.slack_webhook || '');
      setEmailRecipients(settings.email_recipients || '');
      setRegion(settings.data_region || 'us-east-1');
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Settings');
      if (!wasHandled) {
        console.error('Failed to load settings:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await updateSetting(projectId, 'scan_schedule', scanSchedule);
      showSuccess('Scan schedule updated');
    } catch (error) {
      console.error('Failed to save schedule:', error);
      showError(error);
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await Promise.all([
        updateSetting(projectId, 'slack_webhook', slackWebhook),
        updateSetting(projectId, 'email_recipients', emailRecipients)
      ]);
      showSuccess('Notification settings updated');
    } catch (error) {
      console.error('Failed to save notifications:', error);
      showError(error);
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveRegion = async () => {
    setSavingRegion(true);
    try {
      await updateSetting(projectId, 'data_region', region);
      showSuccess('Data residency updated');
    } catch (error) {
      console.error('Failed to save region:', error);
      showError(error);
    } finally {
      setSavingRegion(false);
    }
  };

  const handleGenerateApiKey = async () => {
    setGeneratingKey(true);
    try {
      const response = await generateApiKey(projectId);
      const newKey = {
        id: Date.now(),
        key: response.data.api_key,
        created: response.data.expires_at
      };
      setApiKeys([...apiKeys, newKey]);
      showSuccess('API key generated successfully');
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'Settings');
      if (!wasHandled) {
        console.error('Failed to generate API key:', error);
        showError(error);
      }
    } finally {
      setGeneratingKey(false);
    }
  };

  const fetchDataProcessingPolicy = async () => {
    try {
      const response = await getDataProcessingPolicy();
      if (response.data.policy) {
        setDataPolicy(response.data.policy);
      }
    } catch (error) {
      console.error('Failed to fetch data processing policy:', error);
    }
  };

  const handleSaveDataProcessing = async () => {
    setSavingDataProcessing(true);
    try {
      await updateDataProcessingPolicy(dataPolicy);
      showSuccess('Data processing policy updated');
    } catch (error) {
      console.error('Failed to save data processing policy:', error);
      showError(error);
    } finally {
      setSavingDataProcessing(false);
    }
  };

  const items = [
    {
      key: '1',
      label: 'Scan Schedule',
      children: (
        <Card>
          <div style={{ padding: '20px' }}>
            <h3>Scan Schedule</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Schedule</label>
              <Select
                value={scanSchedule}
                onChange={setScanSchedule}
                style={{ width: 300, marginBottom: '16px' }}
                disabled={loading}
              >
                <Option value="manual">Manual</Option>
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
              </Select>
            </div>
            <Button onClick={handleSaveSchedule} disabled={savingSchedule || loading}>
              {savingSchedule ? <><Spin size="small" /> Saving...</> : 'Save'}
            </Button>
          </div>
        </Card>
      )
    },
    {
      key: '2',
      label: 'Notifications',
      children: (
        <Card>
          <div style={{ padding: '20px' }}>
            <h3>Notifications</h3>
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Configure alert destinations
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Slack Webhook URL</label>
              <Input
                placeholder="Enter Slack webhook URL"
                style={{ marginBottom: '16px' }}
                value={slackWebhook}
                onChange={(e) => setSlackWebhook(e.target.value)}
                disabled={loading}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Email Recipients</label>
              <Input
                placeholder="Enter email recipients (comma-separated)"
                style={{ marginBottom: '16px' }}
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                disabled={loading}
              />
            </div>
            <Button onClick={handleSaveNotifications} disabled={savingNotifications || loading}>
              {savingNotifications ? <><Spin size="small" /> Saving...</> : 'Save'}
            </Button>
          </div>
        </Card>
      )
    },
    {
      key: '3',
      label: 'API Keys',
      children: (
        <Card>
          <div style={{ padding: '20px' }}>
            <h3>API Keys</h3>
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Generate API keys for programmatic access to TrustGrid
            </p>
            <Button onClick={handleGenerateApiKey} disabled={generatingKey || loading} style={{ marginBottom: '16px' }}>
              {generatingKey ? <><Spin size="small" /> Generating...</> : 'Generate Key'}
            </Button>
            {apiKeys.length === 0 ? (
              <p style={{ color: '#888', fontStyle: 'italic' }}>No API keys generated yet</p>
            ) : (
              apiKeys.map((k) => (
                <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <span style={{ fontFamily: 'monospace', flex: 1 }}>{k.key}</span>
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(k.key);
                      showSuccess('API key copied to clipboard');
                    }}
                  />
                </div>
              ))
            )}
          </div>
        </Card>
      )
    },
    {
      key: '4',
      label: 'Data Residency',
      children: (
        <Card>
          <div style={{ padding: '20px' }}>
            <h3>Data Residency</h3>
            <p style={{ color: '#888', marginBottom: '16px' }}>
              Choose where your TrustGrid data is stored
            </p>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Region</label>
              <Select
                value={region}
                onChange={setRegion}
                style={{ width: 300, marginBottom: '16px' }}
                disabled={loading}
              >
                <Option value="us-east-1">US East (N. Virginia)</Option>
                <Option value="eu-west-1">EU West (Ireland)</Option>
                <Option value="ap-southeast-1">Asia Pacific (Singapore)</Option>
              </Select>
            </div>
            <Button onClick={handleSaveRegion} disabled={savingRegion || loading}>
              {savingRegion ? <><Spin size="small" /> Saving...</> : 'Save'}
            </Button>
          </div>
        </Card>
      )
    },
    {
      key: '5',
      label: 'Data Processing',
      children: (
        <Card>
          <div style={{ padding: '20px' }}>
            <h3>Data Processing & Privacy</h3>
            <p style={{ color: '#888', marginBottom: '24px' }}>
              Organization-level policies for event data storage, PII handling, and compliance
            </p>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '12px' }}>Storage Policy</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Default Storage Tier{' '}
                  <Tooltip title="Controls how much event data is stored from connectors">
                    <InfoCircleOutlined />
                  </Tooltip>
                </label>
                <Select
                  value={dataPolicy.default_storage_tier}
                  onChange={(val) => setDataPolicy({...dataPolicy, default_storage_tier: val})}
                  style={{ width: '100%', marginBottom: '8px' }}
                  disabled={loading}
                >
                  <Option value="metadata_only">
                    <strong>Metadata + Traceability</strong> <Tag color="green">Recommended</Tag> - Store minimal info + source references (best for privacy)
                  </Option>
                  <Option value="smart_selective">
                    <strong>Smart Selective</strong> <Tag color="blue">Balanced</Tag> - Mask PII, store references
                  </Option>
                  <Option value="full_events">
                    <strong>Full Events</strong> <Tag color="orange">High Storage</Tag> - Store all (with masking)
                  </Option>
                </Select>
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '12px' }}>PII & Secrets Protection</h4>
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Auto-Mask PII</span>
                <Switch
                  checked={dataPolicy.auto_mask_pii}
                  onChange={(checked) => setDataPolicy({...dataPolicy, auto_mask_pii: checked})}
                  disabled={loading}
                />
              </div>
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Mask Critical Secrets</span>
                <Switch
                  checked={dataPolicy.mask_critical_secrets}
                  onChange={(checked) => setDataPolicy({...dataPolicy, mask_critical_secrets: checked})}
                  disabled={loading}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Critical Secret Policy
                </label>
                <Select
                  value={dataPolicy.critical_secret_policy}
                  onChange={(val) => setDataPolicy({...dataPolicy, critical_secret_policy: val})}
                  style={{ width: '100%' }}
                  disabled={loading}
                >
                  <Option value="store_only_hash">
                    <strong>Store Only Hash</strong> <Tag color="green">Recommended</Tag> - Most secure
                  </Option>
                  <Option value="store_masked">
                    <strong>Store Masked</strong> <Tag color="blue">Partial</Tag> - Show prefix/suffix
                  </Option>
                  <Option value="store_full">
                    <strong>Store Full</strong> <Tag color="red">Not Recommended</Tag> - Complete value
                  </Option>
                </Select>
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '12px' }}>Processing Features</h4>
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>In-Memory Scanning</span>
                <Switch
                  checked={dataPolicy.enable_in_memory_scanning}
                  onChange={(checked) => setDataPolicy({...dataPolicy, enable_in_memory_scanning: checked})}
                  disabled={loading}
                />
              </div>
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>On-Demand Scanning</span>
                <Switch
                  checked={dataPolicy.enable_on_demand_scanning}
                  onChange={(checked) => setDataPolicy({...dataPolicy, enable_on_demand_scanning: checked})}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '12px' }}>Data Retention</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Auto-Archive After (Days)</label>
                <InputNumber
                  min={1}
                  max={3650}
                  value={dataPolicy.auto_archive_days}
                  onChange={(val) => setDataPolicy({...dataPolicy, auto_archive_days: val})}
                  style={{ width: '200px' }}
                  disabled={loading}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Archived Data Retention (Days)</label>
                <InputNumber
                  min={1}
                  max={3650}
                  value={dataPolicy.archived_data_retention_days}
                  onChange={(val) => setDataPolicy({...dataPolicy, archived_data_retention_days: val})}
                  style={{ width: '200px' }}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '12px' }}>Scan Settings</h4>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Default Scan Window{' '}
                  <Tooltip title="Controls which events are evaluated during scans based on their age. Newer events are more likely to have complete traceability fields.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </label>
                <Select
                  value={dataPolicy.default_scan_window}
                  onChange={(val) => setDataPolicy({...dataPolicy, default_scan_window: val})}
                  style={{ width: '100%', marginBottom: '8px' }}
                  disabled={loading}
                >
                  <Option value="last_7_days">
                    <strong>Last 7 Days</strong> <Tag color="green">Recommended</Tag> - Fast scans, recent data
                  </Option>
                  <Option value="last_30_days">
                    <strong>Last 30 Days</strong> <Tag color="blue">Balanced</Tag> - Monthly trend analysis
                  </Option>
                  <Option value="last_90_days">
                    <strong>Last 90 Days</strong> <Tag color="orange">Quarterly</Tag> - Extended coverage
                  </Option>
                  <Option value="all_time">
                    <strong>All Time</strong> <Tag color="red">Slow</Tag> - Complete history (may be slow)
                  </Option>
                </Select>
              </div>
            </div>

            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ marginBottom: '12px' }}>Compliance Modes</h4>
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>GDPR Mode</span>
                <Switch
                  checked={dataPolicy.gdpr_mode}
                  onChange={(checked) => setDataPolicy({...dataPolicy, gdpr_mode: checked})}
                  disabled={loading}
                />
              </div>
              <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>HIPAA Mode</span>
                <Switch
                  checked={dataPolicy.hipaa_mode}
                  onChange={(checked) => setDataPolicy({...dataPolicy, hipaa_mode: checked})}
                  disabled={loading}
                />
              </div>
            </div>

            <Button onClick={handleSaveDataProcessing} disabled={savingDataProcessing || loading}>
              {savingDataProcessing ? <><Spin size="small" /> Saving...</> : 'Save Data Processing Policy'}
            </Button>
          </div>
        </Card>
      )
    }
  ];

  if (loading && !scanSchedule) {
    return (
      <div>
        <ProjectSelector />
        <h2 style={{ marginBottom: '16px' }}>Settings</h2>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <TrustGridPage
      title="Settings"
      subtitle="Configure project settings and data processing policies"
      breadcrumbs={breadcrumbs}
      showRefresh
      onRefresh={() => { fetchSettings(); fetchDataProcessingPolicy(); }}
    >
      <ProjectSelector />

      <Tabs defaultActiveKey="1" items={items} />
    </TrustGridPage>
  );
}

export default Settings;
